import React, { useEffect } from 'react';
import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Footer } from '@fikasio/react-ui-components';
import 'bootstrap/dist/css/bootstrap.min.css';
import AdminLayout from 'components/admin/AdminLayout';
import FeatureGate from 'components/features/FeatureGate';
import { PrivateOutlet } from 'components/PrivateOutlet';
import TopMenu from 'components/TopMenu';
import AppNotices from 'components/UI/AppNotices';
import BasePage from 'components/UI/BasePage';
import { useGetSessionQuery } from 'services/login/api';
import { useAuth } from 'services/login/hooks';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import EntitiesPage from './pages/EntitiesPage';
import EntityPage from './pages/EntityPage';
import EntityUpsertPage from './pages/EntityUpsertPage';
import ItemUpsertPage from './pages/ItemUpsertPage';
import NotificationsPage from './pages/NotificationsPage';
import ObjectivePage from './pages/ObjectivePage';
import ObjectivesPage from './pages/ObjectivesPage';
import OrganizationPage from './pages/OrganizationPage';
import OrganizationsPage from './pages/OrganizationsPage';
import ProjectPage from './pages/ProjectPage';
import ProjectsPage from './pages/ProjectsPage';
import SearchPage from './pages/SearchPage';
import SettingsPage from './pages/SettingsPage';
import TaskPage from './pages/TaskPage';
import TasksPage from './pages/TasksPage';
import TimesheetPage from './pages/TimesheetPage';
import TemplateUpsertPage from './pages/TemplateUpsertPage';
import TemplatePage from './pages/TemplatePage';
import TemplatesPage from './pages/TemplatesPage';
import FeaturesPage from './pages/FeaturesPage';
import UsersPage from './pages/UsersPage';
import Page404 from './pages/Page404';
import { FEATURES } from 'services/features/catalog';
import envvars from 'utils/envvars';
import { setActiveTimeZone } from 'utils/timezone';
import links from './utils/links';
import './App.css';

const NotFoundRoute = () => {
  const auth = useAuth();

  if (!auth.isAuthReady) {
    return null;
  }

  if (!auth.user) {
    return <Page404 />;
  }

  return (
    <BasePage>
      <Page404 />
    </BasePage>
  );
};

const App = () => {
  const location = useLocation();
  const auth = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    setActiveTimeZone(auth.user?.timezone);
  }, [auth.user?.timezone]);

  useGetSessionQuery(undefined, {
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  return (
    <GoogleOAuthProvider
      clientId={envvars.googleOAuthClientId}
    >
      <div className="App" id="App">
        <AppNotices />
        <div className="siteContent">
          <TopMenu />
          <Routes>
            <Route element={<LoginPage />} path={links.paths.login} />
            <Route element={<SignupPage />} path={links.paths.signup} />
            <Route element={<PrivateOutlet />}>
              <Route element={<HomePage />} path="/" />
              <Route element={<HomePage />} path={links.paths.home} />
              <Route element={<EntityUpsertPage />} path={links.paths.entityUpsert} />
              <Route element={<ItemUpsertPage />} path={links.paths.itemUpsert} />
              <Route element={<EntityPage />} path={links.paths.entity} />
              <Route element={<EntitiesPage />} path={links.paths.entities} />
              <Route element={<NotificationsPage />} path={links.paths.notifications} />
              <Route
                element={(
                  <FeatureGate feature={FEATURES.objectives}>
                    <ObjectivePage />
                  </FeatureGate>
                )}
                path={links.paths.objective}
              />
              <Route
                element={(
                  <FeatureGate feature={FEATURES.objectives}>
                    <ObjectivesPage />
                  </FeatureGate>
                )}
                path={links.paths.objectives}
              />
              <Route element={<OrganizationPage />} path={links.paths.organization} />
              <Route element={<OrganizationsPage />} path={links.paths.organizations} />
              <Route element={<ProjectPage />} path={links.paths.project} />
              <Route element={<ProjectsPage />} path={links.paths.projects} />
              <Route element={<SearchPage />} path={links.paths.search} />
              <Route element={<SettingsPage />} path={links.paths.settings} />
              <Route element={<SettingsPage />} path={links.paths.settingsSection} />
              <Route
                element={(
                  <FeatureGate feature={FEATURES.tasks}>
                    <TaskPage />
                  </FeatureGate>
                )}
                path={links.paths.task}
              />
              <Route
                element={(
                  <FeatureGate feature={FEATURES.tasks}>
                    <TasksPage />
                  </FeatureGate>
                )}
                path={links.paths.tasks}
              />
              <Route
                element={(
                  <FeatureGate feature={FEATURES.timesheet}>
                    <TemplateUpsertPage />
                  </FeatureGate>
                )}
                path={links.paths.templateUpsert}
              />
              <Route
                element={(
                  <FeatureGate feature={FEATURES.timesheet}>
                    <TemplatePage />
                  </FeatureGate>
                )}
                path={links.paths.template}
              />
              <Route
                element={(
                  <FeatureGate feature={FEATURES.timesheet}>
                    <TemplatesPage />
                  </FeatureGate>
                )}
                path={links.paths.templates}
              />
              <Route
                element={(
                  <FeatureGate feature={FEATURES.timesheet}>
                    <TimesheetPage />
                  </FeatureGate>
                )}
                path={links.timesheet()}
              />
              <Route element={<AdminLayout />} path={links.paths.admin}>
                <Route element={<FeaturesPage />} path="features" />
                <Route element={<UsersPage />} path="users" />
              </Route>
              <Route element={<Navigate replace to={links.users()} />} path="/users" />
            </Route>
            <Route element={<NotFoundRoute />} path="*" />
          </Routes>
          {
            (
              (location.pathname === '/' && !auth.user)
              || (location.pathname === links.paths.home && !auth.user)
              || location.pathname === links.paths.login
              || location.pathname === links.paths.signup
            ) && (
              <Footer
                childrenTop={[
                  <>
                    &copy;
                    {new Date().getFullYear()}&nbsp;
                    <a
                      href={envvars.urlWebsite}
                      key="link"
                      style={{ textDecoration: 'none' }}
                    >
                      fikas.io
                    </a>
                  </>,
                  <span key="version">{t('version', { version: envvars.version })}</span>,
                ]}
                childrenLeft={[
                  <a href={envvars.urlWebsite} key="link">{t('home')}</a>,
                  <a href={envvars.urlPrivacy} key="privacy">{t('privacy')}</a>,
                  <a href={envvars.urlToS} key="tos">{t('termsOfService')}</a>,
                ]}
                childrenRight={[
                  <a href={envvars.urlDocumentation} key="documentation">{t('documentation')}</a>,
                ]}
              />
            )
          }
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default App;
